<template>
  <v-container
    id="logbook-general-view"
    fluid
    tag="section"
  >
    <material-card
      :avatar="require('@/assets/general.png')"
      color="accent"
      :title="page_title"
      help="logbook_general"
    >
      <v-card-title>
        <v-btn
          v-shortkey.once="['f2']"
          color="primary"
          :disabled="!editor"
          class="mb-2"
          @shortkey="edit({id: -1})"
          @click="edit({id: -1})"
        >
          {{ $t('new_item',{ item: $t('flight') }) }}
        </v-btn>
        <logbook-general-stat
          :user-id="user_id"
        />
        <export-btn
          source-type="logbook_general"
          :params="{ user_id: user_id}"
        />
        <export-btn
          source-type="logbook_general"
          export-type="pdf"
          :params="{ user_id: user_id, year: filter_year}"
        />
        <upload-files
          source-type="logbook_general"
          is-import
          @success="load"
        />
        <v-row class="ma-0">
          <v-spacer v-if="!$vuetify.breakpoint.mdAndDown" />
          <v-select
            v-model="filter_year"
            :items="years"
            :label="$t('fields.season')"
            hide-details
            class="mx-2"
          />
          <data-selector
            v-if="profile.has_role.instructor"
            v-model="user_id"
            field-name="user"
            source-type="pilots"
            hide-details
            class="mx-2"
          />
          <v-text-field
            v-model="search"
            append-icon="$search"
            class="ml-auto"
            hide-details
            :label="$t('search_records')"
            single-line
            style="max-width: 400px;"
          />
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="computed_headers"
          :items="items"
          :loading="loading"
          :loading-text="$t('loading')"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="footerProps"
        >
          <template #item.takeoff_time="{item}">
            {{ item.takeoff_airport }} - {{ item.takeoff_time | timeFormat }}
          </template>

          <template #item.landing_time="{item}">
            {{ item.landing_airport }} - {{ item.landing_time | timeFormat }}
          </template>

          <template #item.flight_time="{item}">
            {{ item.flight_time | timeFormat }}
          </template>

          <template #item.actions="{item}">
            <v-btn
              v-if="item.flight !==null && item.flight.ogn_url != null"
              icon
              color="blue"
              :href="item.flight.ogn_url"
              target="_blank"
            >
              <v-icon v-html="'$map'" />
            </v-btn>
            <flight-informations
              v-if="item.flight !==null"
              :flight="item"
            />
            <tooltip-btn
              v-for="(action, i) in actions"
              :key="i"
              :show="action.visible ? action.visible(item) : true"
              :color="action.color"
              :disabled="action.disable"
              :icon="action.icon"
              :tooltip="action.tooltip"
              @click="action.click ? action.click(item) : ''"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, sync } from 'vuex-pathify'
  import FlightInformations from '@/layouts/default/infos/FlightInfo'
  import LogbookGeneralStat from '@/layouts/default/infos/LogbookGeneralStat'

  export default {
    name: 'LogbookGeneral',
    components: { LogbookGeneralStat, FlightInformations },
    data () {
      return {
        items: [],
        // table
        footerProps: {
          itemsPerPageOptions: [10, 20, 25, 50, 100],
        },
        total: 0,
        timer: null,
        user_id: null,
        years: [],
        filter_year: new Date().getFullYear(),
      }
    },
    computed: {
      loading: get('app/loading'),
      profile: get('auth/profile'),
      options: sync('logbookGeneral/options'),
      search: sync('logbookGeneral/search'),
      headers () {
        return [
          {
            text: this.$t('fields.date'),
            align: 'start',
            sortable: true,
            value: 'date',
          },
          {
            text: this.$t('fields.takeoff'),
            align: 'start',
            sortable: false,
            value: 'takeoff_time',
          },
          {
            text: this.$t('fields.landing'),
            align: 'start',
            sortable: false,
            value: 'landing_time',
          },
          {
            hide_small: true,
            text: this.$t('fields.type'),
            align: 'start',
            sortable: false,
            value: 'plane_type',
          },
          {
            text: this.$t('fields.plane_regnum'),
            align: 'start',
            sortable: false,
            value: 'plane_regnum',
          },
          {
            hide_small: true,
            text: this.$t('fields.flight_time'),
            align: 'start',
            sortable: false,
            value: 'flight_time',
          },
          {
            hide_small: true,
            text: this.$t('fields.takeoff_number'),
            align: 'start',
            sortable: false,
            value: 'takeoff_day',
          },
          {
            hide_small: true,
            text: this.$t('fields.pic_name'),
            align: 'start',
            sortable: false,
            value: 'pic_name',
          },
          {
            text: this.$t('actions'),
            value: 'actions',
            sortable: false,
            align: 'right',
          },
        ]
      },
      actions () {
        return [
          { color: 'edit', icon: '$edit', tooltip: 'tooltip_edit', click: this.edit, disable: !this.editor },
        ]
      },
      page_title () { return this.$t('logbook') + ' ' + this.$t('general') },
      editor () { return this.user_id === this.profile.user_id },
      computed_headers () {
        return this.headers.filter(h => h.hide_small !== this.$vuetify.breakpoint.mdAndDown)
      },
    },
    watch: {
      search: {
        handler () {
          if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
          }
          this.timer = setTimeout(() => {
            this.options.page = 1
            this.load()
          }, 800)
        },
        deep: true,
      },
      options: {
        handler () {
          this.load()
        },
        deep: true,
      },
      user_id: {
        handler () {
          this.load()
        },
        deep: true,
      },
      filter_year: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    mounted () {
      this.user_id = this.profile.user_id
      for (let i = 1950; i <= new Date().getFullYear(); i++) {
        this.years.unshift(i)
      }
    },
    methods: {
      getList: call('logbookGeneral/search'),
      getFlightInfo: call('flightGeneral/info'),
      async load () {
        try {
          const { sortBy, sortDesc, page, itemsPerPage } = this.options
          await this.getList({
            page: page,
            per_page: itemsPerPage,
            sort: {
              field: sortBy,
              order: sortDesc,
            },
            search: this.search,
            user_id: this.user_id,
            year: this.filter_year,
          }).then(response => {
            this.items = response.data
            this.total = response.total
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
        }
      },
      edit (item) {
        this.$router.push(`/logbook/general/${item.id}`).catch(() => {})
      },
      visibleFlightInfo (item) {
        return item.flight_id !== null
      },
    },
  }
</script>
